import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown } from 'lucide-react';

const AccordionItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-[#D4CFC7]">
      <button
        onClick={onClick}
        className="w-full py-5 flex justify-between items-start text-left hover:bg-[#F2EEE7]/50 transition-colors px-1"
      >
        <span className="font-medium text-[#5C4033] pr-4">{question}</span>
        <ChevronDown 
          className={`w-5 h-5 text-[#7A1E2C] flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[500px] pb-5' : 'max-h-0'}`}
      >
        <p className="text-[#6B5B4F] leading-relaxed px-1">{answer}</p>
      </div>
    </div>
  );
};

const FAQSection = ({ title, items }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="mb-12">
      <h2 className="text-2xl font-serif font-bold text-[#5C4033] mb-6">{title}</h2>
      <div className="bg-white rounded-lg border border-[#D4CFC7] px-6">
        {items.map((item, index) => (
          <AccordionItem
            key={index}
            question={item.q}
            answer={item.a}
            isOpen={openIndex === index}
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
          />
        ))}
      </div>
    </div>
  );
};

const FAQ = () => {
  const paymentsAndFees = [
    {
      q: "How does payment work?",
      a: "Payment is split into two parts. A 50% deposit is collected once the drop is confirmed and the order is ready to be placed. The remaining balance is payable upon collection or delivery of your items. We accept payment via PayNow and CardUp."
    },
    {
      q: "What fees does Sharedequal charge?",
      a: "Sharedequal charges a small service fee per user per drop. For orders that include oversized items such as mallets, the fee is $12 per user. For all other items, the fee is $8 per user. If your order includes both oversized and non-oversized items, the fee is $8. This fee covers order coordination, customs handling, and local delivery management."
    },
    {
      q: "Are there any hidden fees?",
      a: "No. The costs you see are the product price, your share of shipping, any applicable customs duties or GST, and the Sharedequal service fee. All costs are communicated transparently before you confirm your order."
    },
    {
      q: "When do I find out my total cost?",
      a: "Once the drop window closes and all orders are confirmed, we calculate the final breakdown including product cost, shared shipping, and any duties. You receive a clear summary before the deposit is collected."
    },
    {
      q: "What currency are prices in?",
      a: "Product prices are listed in the supplier's currency (typically GBP or USD). Your payment to Sharedequal will be in SGD. The exchange rate applied will be communicated with your order summary."
    }
  ];

  const shippingAndLogistics = [
    {
      q: "How does shared shipping work?",
      a: "Instead of each person paying full international shipping individually, orders from the same brand are consolidated into a single shipment. The total shipping cost is then shared fairly among all participants based on the allocation method for that drop."
    },
    {
      q: "How long does shipping take?",
      a: "Timelines vary depending on the brand and their production schedule. Custom items like mallets may take longer to produce. Once shipped, delivery to Singapore typically takes 10–14 business days. We provide regular updates throughout the process."
    },
    {
      q: "Do I need to handle customs clearance?",
      a: "No. Sharedequal manages the customs clearance process. Any customs duties or GST charges assessed on the shipment are allocated fairly among participants based on item value and communicated before final payment."
    },
    {
      q: "How is shipping cost allocated?",
      a: "Shipping is allocated fairly based on the items in the drop. The specific methodology may vary depending on the order mix. The allocation is always transparent and shared with participants before payment."
    },
    {
      q: "Can I choose my own shipping method?",
      a: "Currently, Sharedequal coordinates shipping using the brand's courier partners or our own arrangements to ensure the best rates for consolidated shipments. Individual shipping preferences are not available at this time."
    },
    {
      q: "Where do I collect my items?",
      a: "Collection arrangements are communicated for each drop. Typically, items are available for collection at Singapore Polo Club or can be personally delivered by arrangement."
    }
  ];

  const dropStatusAndTiming = [
    {
      q: "What is a \"drop\"?",
      a: "A drop is a curated release window where we open orders for a specific brand. Players register their interest, submit their item details, and once we reach the minimum number of participants, the order is placed."
    },
    {
      q: "How do I know when a drop is happening?",
      a: "Current and upcoming drops are shown on the website under the Release Calendar. You can also follow us on Instagram (@sharedequal) or message us on WhatsApp to be notified."
    },
    {
      q: "What happens if the drop doesn't reach the minimum?",
      a: "If a drop does not reach the minimum number of participants required, the drop will not proceed and no deposits will be collected. You will be notified and can join a future drop."
    },
    {
      q: "How long does a drop window stay open?",
      a: "Drop windows vary but are typically open for 2–4 weeks. The closing date is always communicated clearly when the drop is announced."
    },
    {
      q: "Can I join a drop after the window has closed?",
      a: "Generally, no. Once the drop window closes, orders are finalised and placed with the brand. If you miss a window, you are welcome to join the next release."
    },
    {
      q: "What does \"currently curating\" mean?",
      a: "It means we are in the process of selecting products and coordinating with brands for the next release. During this time, the site may be quieter — this is by design. We focus on quality over frequency."
    }
  ];

  const cancellationsAndReturns = [
    {
      q: "Can I cancel my order?",
      a: "You may withdraw your interest before the drop is confirmed and the order is placed with the brand. Once the drop is confirmed and your deposit has been collected, cancellations are not possible as the order has been committed on behalf of all participants."
    },
    {
      q: "What happens if I withdraw after confirmation?",
      a: "If you withdraw after the order has been placed, you may remain liable for costs already incurred. This includes any portion of shipping, product costs, or duties that have been committed. This policy exists to protect other participants in the same drop."
    },
    {
      q: "Are deposits refundable?",
      a: "No. Once the drop is confirmed and the order is placed with the brand, deposits are non-refundable. This reflects the commitment made on behalf of all participating users."
    },
    {
      q: "What if there's a problem with my product?",
      a: "Any issues relating to product quality, defects, sizing, or warranties should be raised directly with the brand or supplier. Sharedequal can assist with communication where possible, but responsibility for product-related matters lies with the brand. We recommend checking sizing and specifications carefully before confirming your order."
    },
    {
      q: "Can I return or exchange an item?",
      a: "Returns and exchanges are subject to the brand's own return policy. Since items are shipped internationally as part of a consolidated order, return shipping costs and logistics are the responsibility of the individual user. Sharedequal can help facilitate communication with the brand but does not process returns directly."
    }
  ];

  const trustAndSecurity = [
    {
      q: "Is Sharedequal a legitimate business?",
      a: "Yes. Sharedequal is operated in Singapore and has successfully completed consolidated orders for polo players at Singapore Polo Club. Our drop history and completed releases are shown on the website as part of our track record."
    },
    {
      q: "How do I know my money is safe?",
      a: "Deposits are collected only after the drop is confirmed and the order is ready to be placed. Sharedequal does not hold funds speculatively. All costs are communicated transparently, and payment is collected in two stages (deposit and final balance) to minimise risk for participants."
    },
    {
      q: "Who am I buying from?",
      a: "You are purchasing products from the brand or supplier directly. Sharedequal facilitates the coordination, consolidated shipping, and local delivery. We do not manufacture, resell, or warehouse products."
    },
    {
      q: "Is my personal information safe?",
      a: "Yes. Sharedequal collects only the information necessary to coordinate your order. We do not sell, rent, or trade personal data. For full details, please refer to our Privacy Policy."
    },
    {
      q: "How do I know other participants are real?",
      a: "Sharedequal manages all participant communication privately. You do not need to interact with other participants. All coordination is handled by Sharedequal directly. Participant details are kept confidential."
    },
    {
      q: "What if Sharedequal can't fulfil the order?",
      a: "If for any reason a drop cannot proceed after deposits have been collected (for example, supplier cancellation), deposits will be refunded in full. Sharedequal will communicate promptly with all affected participants."
    }
  ];

  return (
    <div className="min-h-screen bg-[#F2EEE7]" data-testid="faq-page">
      {/* Navigation */}
      <nav className="border-b border-[#D4CFC7] bg-[#F2EEE7]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-[#5C4033] hover:text-[#7A1E2C] transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-serif font-bold text-[#5C4033] mb-4">Frequently Asked Questions</h1>
        <p className="text-[#6B5B4F] text-lg mb-12">Everything you need to know about how Sharedequal works.</p>

        <FAQSection title="Payments & Fees" items={paymentsAndFees} />
        <FAQSection title="Shipping & Logistics" items={shippingAndLogistics} />
        <FAQSection title="Drop Status & Timing" items={dropStatusAndTiming} />
        <FAQSection title="Cancellations & Returns" items={cancellationsAndReturns} />
        <FAQSection title="Trust & Security" items={trustAndSecurity} />

        <div className="mt-12 pt-8 border-t border-[#D4CFC7]">
          <p className="text-[#6B5B4F] mb-4">Still have questions?</p>
          <Link 
            to="/contact" 
            className="inline-flex items-center gap-2 text-[#7A1E2C] hover:underline font-medium"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
